import { useEffect } from "react";
import { create } from "zustand";
import { readClientId } from "@/lib/session/sessionWatcher";
import {
  createSpace as apiCreateSpace,
  fetchCurrentPlan,
  fetchMyOrgInvitations,
  fetchOrgs,
  fetchOrgSpaces,
  fetchOrgUsage,
  type CurrentPlan,
  type MyOrgInvitation,
  type Org,
  type OrgUsage,
  type Space,
} from "@/lib/api/organizations";

const ACTIVE_ORG_KEY = "mycorr.activeOrgId";
const COLLAPSED_KEY = "mycorr.collapsedSpaces";
const TREE_COLLAPSED_KEY = "mycorr.spacesTreeCollapsed";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

/// One picker row: the space plus its ancestor names, root-first. The picker
/// renders ancestors muted and the leaf bright ("Product Analytics / Data
/// Quality / Q3 Cleanup"), per the approved mocks — a flat list, never an
/// indented tree.
export interface SpacePath {
  space: Space;
  ancestors: string[];
}

/// Depth-first flattening (children under their parent, names sorted) so the
/// rail and picker agree on order. Call this inside a `useMemo` keyed on
/// `spaces` — NEVER from a zustand selector: it allocates a new array every
/// call, and the store compares with `Object.is`, so a selector returning it
/// re-renders forever (React #185). Cycles cannot exist (the server refuses
/// cross-org parents and a space cannot parent itself), but the visited set
/// keeps a corrupt row from hanging the UI.
/// The WORKING view: everything except what governance alone reaches (D3/D13
/// metadata). The full list stays in the store — org settings ▸ Spaces governs
/// it, the audit log names rows from it, and a governor still reaches any space
/// at `/spaces/:id` to un-seal it. Allocates, so the `flattenSpaceTree` rule
/// applies: inside a `useMemo`, never from a selector.
/// What the working surfaces draw: no governed-only rows, and no space inside
/// its deletion window — that one lives in the Trash, and its whole subtree
/// went with it (D53), so nothing live is left under it to re-root.
export function visibleSpaces(spaces: Space[]): Space[] {
  return spaces.filter((s) => !s.governed_only && !s.deletion_scheduled_at);
}

/// The path to one space, root-first and ending with the space itself — what
/// a breadcrumb walks back up. Empty when the id is not in `spaces` (a space
/// deleted or narrowed away under an open filter). The visited set is the
/// same corrupt-row guard `flattenSpaceTree` carries.
export function spaceTrail(spaces: Space[], spaceId: string): Space[] {
  const byId = new Map(spaces.map((sp) => [sp.space_id, sp]));
  const trail: Space[] = [];
  const visited = new Set<string>();
  let current = byId.get(spaceId);
  while (current && !visited.has(current.space_id)) {
    visited.add(current.space_id);
    trail.unshift(current);
    current = current.parent_space_id
      ? byId.get(current.parent_space_id)
      : undefined;
  }
  return trail;
}

export function flattenSpaceTree(spaces: Space[]): SpacePath[] {
  const byParent = new Map<string | null, Space[]>();
  for (const sp of spaces) {
    const key = sp.parent_space_id ?? null;
    const bucket = byParent.get(key) ?? [];
    bucket.push(sp);
    byParent.set(key, bucket);
  }
  for (const bucket of byParent.values()) {
    bucket.sort((a, b) => a.name.localeCompare(b.name));
  }
  const out: SpacePath[] = [];
  const visited = new Set<string>();
  const walk = (parent: string | null, ancestors: string[]) => {
    for (const space of byParent.get(parent) ?? []) {
      if (visited.has(space.space_id)) continue;
      visited.add(space.space_id);
      out.push({ space, ancestors });
      walk(space.space_id, [...ancestors, space.name]);
    }
  };
  walk(null, []);
  // Orphans (visible child of an invisible parent): surface at top level
  // rather than dropping them.
  for (const sp of spaces) {
    if (!visited.has(sp.space_id)) {
      visited.add(sp.space_id);
      out.push({ space: sp, ancestors: [] });
    }
  }
  return out;
}

let flattenedFrom: Space[] | null = null;
let flattened: SpacePath[] = [];

/// `flattenSpaceTree(visibleSpaces(spaces))`, shared by every surface that
/// draws the tree and rebuilt only when the spaces array changes.
export function visibleSpacePaths(spaces: Space[]): SpacePath[] {
  if (spaces !== flattenedFrom) {
    flattenedFrom = spaces;
    flattened = flattenSpaceTree(visibleSpaces(spaces));
  }
  return flattened;
}

export function useVisibleSpacePaths(): SpacePath[] {
  return visibleSpacePaths(useOrgContextStore((s) => s.spaces));
}

/// The spaces under `rootId` (every space for null) with their depth below
/// it; `flattenSpaceTree` keeps a subtree contiguous after its root.
export function spaceSubtree(
  paths: SpacePath[],
  rootId: string | null,
): { path: SpacePath; depth: number }[] {
  if (rootId === null) {
    return paths.map((path) => ({ path, depth: path.ancestors.length }));
  }
  const start = paths.findIndex((p) => p.space.space_id === rootId);
  if (start < 0) return [];
  const rootDepth = paths[start].ancestors.length;
  const below: { path: SpacePath; depth: number }[] = [];
  for (
    let i = start + 1;
    i < paths.length && paths[i].ancestors.length > rootDepth;
    i++
  ) {
    below.push({
      path: paths[i],
      depth: paths[i].ancestors.length - rootDepth - 1,
    });
  }
  return below;
}

/// A space's ancestors, root-first, without the space itself.
export function spaceAncestors(spaces: Space[], spaceId: string): Space[] {
  return spaceTrail(spaces, spaceId).slice(0, -1);
}

interface OrgContextState {
  orgs: Org[];
  /// The active context. Every models/search request carries it (S6c
  /// narrowing); direct shares and catalog stay visible regardless (D18/D20).
  activeOrgId: string | null;
  /// The account that chose `activeOrgId`. A tab outlives its session when
  /// another tab logs out and someone else signs in; `activeOrgForRequests`
  /// refuses to attach an org chosen under a different account.
  sessionClientId: string | null;
  spaces: Space[];
  spacesLoading: boolean;
  /// Rail collapse state, persisted — space_id -> collapsed.
  collapsed: Record<string, boolean>;
  /// The whole tree folded under the rail's Spaces row, persisted.
  treeCollapsed: boolean;
  /// Rail filter for the overview list. Session-only, cleared on org switch.
  selectedSpaceId: string | null;
  /// The active org's plan, for surfaces that need its limits (the new-org
  /// welcome strip). Null while loading or when the org has no recorded
  /// period. The switcher's badge does NOT read this — it labels every row
  /// from `Org.plan_name`, so the trigger and the menu agree.
  plan: CurrentPlan | null;
  /// The active org's metered allowances — the one read the banner, the rail
  /// and the panel all share. Null while loading or when the read failed.
  usage: OrgUsage | null;

  /// Bumped after an image upload/removal so every OrgMark re-fetches.
  imageVersion: number;

  /// Invitations awaiting THIS user's answer (D43). Never merged into `orgs`
  /// — an unaccepted org must not become switchable.
  pendingInvites: MyOrgInvitation[];
  /// Whether `pendingInvites` has ever been fetched. An empty list means
  /// "nothing pending" only once this is true; before that it means "unknown",
  /// and callers must not read absence as a settled invitation.
  invitesLoaded: boolean;
  /// Whether `orgs` has ever been fetched. Before that an empty list means
  /// "unknown", so a surface must not resolve a name against it — rendering a
  /// miss as a placeholder label states a fact that was never read.
  orgsLoaded: boolean;

  init: () => Promise<void>;
  refreshInvites: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  adoptOrg: (org: Org) => void;
  /// Re-fetches the org list (rename, image flag) without touching the
  /// active context, spaces or plan.
  refreshOrgs: () => Promise<void>;
  bumpImageVersion: () => void;
  refreshSpaces: () => Promise<void>;
  /// On a timer, and immediately after a refusal so the banner does not
  /// arrive a minute after the toast.
  refreshUsage: () => Promise<void>;
  toggleCollapsed: (spaceId: string) => void;
  /// Collapse all folds every branch with the tree; expand all opens them all.
  toggleTreeCollapsed: () => void;
  /// Opens the tree under the Spaces row, leaving each branch as it was.
  openTree: () => void;
  setSelectedSpace: (spaceId: string | null) => void;
  createSpace: (
    name: string,
    options?: {
      parentSpaceId?: string;
      color?: string;
      sealed?: boolean;
      /// `open` | `restricted` | `sealed` (D39); wins over `sealed`.
      classification?: string;
      managers?: string[];
    },
  ) => Promise<Space | null>;

  activeOrg: () => Org | undefined;
  spaceById: (spaceId: string | null | undefined) => Space | undefined;
}

/// Guarantees the org context is loaded on whatever route the caller renders
/// on. The app shell and the canvas route are SIBLING routes, so each has to
/// ask: `orgs` was empty for the whole canvas route while `init()` lived in the
/// sidebar alone, which left every org name there unresolvable.
export function useEnsureOrgContext(): void {
  const init = useOrgContextStore((s) => s.init);
  useEffect(() => {
    void init();
  }, [init]);
}

type SetState = (partial: Partial<OrgContextState>) => void;
type GetState = () => OrgContextState;

/// In-flight `init()`, shared by every mount that asks for it. Both the app
/// shell and the canvas route need the org context, and they can mount
/// together, so without this the two would each fetch the whole list.
let inFlightInit: Promise<void> | null = null;

async function runInit(set: SetState, get: GetState): Promise<void> {
  void get().refreshInvites();
  const orgs = await fetchOrgs();
  const stored = localStorage.getItem(ACTIVE_ORG_KEY);
  const active =
    orgs.find((o) => o.org_id === stored)?.org_id ??
    orgs.find((o) => o.is_personal)?.org_id ??
    orgs[0]?.org_id ??
    null;
  set({
    orgs,
    activeOrgId: active,
    orgsLoaded: true,
    sessionClientId: readClientId(),
  });
  if (active) {
    localStorage.setItem(ACTIVE_ORG_KEY, active);
    // Plans key on the org (S4a), so the label follows the active context.
    const [plan, usage] = await Promise.all([
      fetchCurrentPlan(active),
      fetchOrgUsage(active),
      get().refreshSpaces(),
    ]);
    if (get().activeOrgId === active) set({ plan, usage });
  }
}

export const useOrgContextStore = create<OrgContextState>((set, get) => ({
  orgs: [],
  activeOrgId: null,
  sessionClientId: null,
  spaces: [],
  spacesLoading: false,
  collapsed: readJson<Record<string, boolean>>(COLLAPSED_KEY, {}),
  treeCollapsed: readJson<boolean>(TREE_COLLAPSED_KEY, false),
  selectedSpaceId: null,
  plan: null,
  usage: null,
  imageVersion: 0,
  pendingInvites: [],
  invitesLoaded: false,
  orgsLoaded: false,

  init: async () => {
    if (inFlightInit) return inFlightInit;
    inFlightInit = (async () => {
      try {
        await runInit(set, get);
      } finally {
        inFlightInit = null;
      }
    })();
    return inFlightInit;
  },

  refreshOrgs: async () => {
    const orgs = await fetchOrgs();
    // `fetchOrgs` answers [] on failure too, and every account has at least a
    // personal org, so an empty list means the read failed — keep what we had.
    if (orgs.length === 0) return;
    const activeId = get().activeOrgId;
    const stillAMember = orgs.some((o) => o.org_id === activeId);
    set({ orgs, orgsLoaded: true });
    // Removed from the org we were standing in. The switcher renders nothing
    // for an active org it cannot find and every scoped request 403s, so fall
    // back rather than leave the UI in a context that no longer exists.
    if (activeId && !stillAMember) {
      const fallback = orgs.find((o) => o.is_personal) ?? orgs[0];
      if (fallback) await get().switchOrg(fallback.org_id);
    }
  },

  refreshInvites: async () => {
    const pendingInvites = await fetchMyOrgInvitations();
    set({ pendingInvites, invitesLoaded: true });
  },

  /// Put an org into the list before a refresh has proven it. The switcher
  /// renders nothing when the active org is absent from `orgs`, so a create
  /// whose refresh lost the read-after-write race would blank the sidebar and
  /// read as "it never switched". The next refresh supersedes this row.
  adoptOrg: (org) =>
    set((s) =>
      s.orgs.some((o) => o.org_id === org.org_id)
        ? s
        : { orgs: [...s.orgs, org] },
    ),

  bumpImageVersion: () => set({ imageVersion: get().imageVersion + 1 }),

  switchOrg: async (orgId) => {
    if (orgId === get().activeOrgId) return;
    localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    set({
      activeOrgId: orgId,
      sessionClientId: readClientId(),
      spaces: [],
      selectedSpaceId: null,
      plan: null,
      usage: null,
    });
    const [plan, usage] = await Promise.all([
      fetchCurrentPlan(orgId),
      fetchOrgUsage(orgId),
      get().refreshSpaces(),
    ]);
    // Ignore a slow response that lost a race with another switch.
    if (get().activeOrgId === orgId) set({ plan, usage });
  },

  refreshUsage: async () => {
    const org = activeOrgForRequests();
    if (org.kind !== "org") return;
    const orgId = org.orgId;
    const usage = await fetchOrgUsage(orgId);
    // A failed read must not flicker the banner off; keep what we had.
    if (usage && get().activeOrgId === orgId) set({ usage });
  },

  refreshSpaces: async () => {
    const org = activeOrgForRequests();
    if (org.kind !== "org") return;
    const orgId = org.orgId;
    set({ spacesLoading: true });
    const spaces = await fetchOrgSpaces(orgId);
    // Ignore a slow response that lost a race with an org switch.
    if (get().activeOrgId === orgId) {
      // A refresh can be the first news that access went away, so the rail
      // filter has to let go of a space that is no longer in the list —
      // otherwise the overview stays scoped to something the user cannot see.
      const selected = spaces.find((s) => s.space_id === get().selectedSpaceId);
      const selectedSpaceId =
        selected && !selected.deletion_scheduled_at ? selected.space_id : null;
      set({ spaces, spacesLoading: false, selectedSpaceId });
    }
  },

  toggleCollapsed: (spaceId) => {
    const collapsed = {
      ...get().collapsed,
      [spaceId]: !get().collapsed[spaceId],
    };
    writeJson(COLLAPSED_KEY, collapsed);
    set({ collapsed });
  },

  toggleTreeCollapsed: () => {
    const treeCollapsed = !get().treeCollapsed;
    const collapsed: Record<string, boolean> = {};
    if (treeCollapsed) {
      for (const sp of get().spaces) {
        if (sp.parent_space_id) collapsed[sp.parent_space_id] = true;
      }
    }
    writeJson(COLLAPSED_KEY, collapsed);
    writeJson(TREE_COLLAPSED_KEY, treeCollapsed);
    set({ treeCollapsed, collapsed });
  },

  openTree: () => {
    if (!get().treeCollapsed) return;
    writeJson(TREE_COLLAPSED_KEY, false);
    set({ treeCollapsed: false });
  },

  setSelectedSpace: (spaceId) => {
    const { spaces, collapsed } = get();
    // Reached from outside the rail (a crumb, a link), a space is never left
    // folded away inside a collapsed parent.
    const folded = spaceId
      ? spaceAncestors(spaces, spaceId).filter((a) => collapsed[a.space_id])
      : [];
    if (folded.length === 0) {
      set({ selectedSpaceId: spaceId });
      return;
    }
    const unfolded = { ...collapsed };
    for (const ancestor of folded) delete unfolded[ancestor.space_id];
    writeJson(COLLAPSED_KEY, unfolded);
    set({ selectedSpaceId: spaceId, collapsed: unfolded });
  },

  createSpace: async (name, options) => {
    const orgId = get().activeOrgId;
    if (!orgId) return null;
    const created = await apiCreateSpace(orgId, name, options);
    if (created) {
      await get().refreshSpaces();
    }
    return created;
  },

  activeOrg: () => get().orgs.find((o) => o.org_id === get().activeOrgId),

  spaceById: (spaceId) =>
    spaceId ? get().spaces.find((s) => s.space_id === spaceId) : undefined,
}));

/// The pickable swatch vocabulary (create dialog + settings header) — also
/// the fallback wheel for spaces with no stored color.
export const SPACE_PALETTE = [
  "sky",
  "ice",
  "frost",
  "dawn",
  "aurora",
  "glow",
  "ember",
  "berry",
];

export function spaceColorToken(spaceId: string): string {
  let hash = 0;
  for (let i = 0; i < spaceId.length; i++) {
    hash = (hash * 31 + spaceId.charCodeAt(i)) >>> 0;
  }
  return SPACE_PALETTE[hash % SPACE_PALETTE.length];
}

/// The space's dot color: the stored pick when there is one, else the stable
/// id-derived slot (what every pre-color space has always rendered).
export function spaceColor(space: Pick<Space, "space_id" | "color">): string {
  return space.color || spaceColorToken(space.space_id);
}

/// Thrown by a write that would have carried an org this tab no longer owns.
export class StaleSessionError extends Error {
  constructor() {
    super("This tab's session changed; reload before retrying.");
    this.name = "StaleSessionError";
  }
}

/// What an outgoing request should do about org scope. `stale` is separate from
/// `unscoped` on purpose: omitting the org WIDENS a read, so a request made
/// under someone else's session has to be dropped rather than narrowed to
/// nothing. The id is fetched after the store may have initialised, so an
/// unknown owner adopts the current session on first use.
export type RequestOrg =
  { kind: "org"; orgId: string } | { kind: "unscoped" } | { kind: "stale" };

export function activeOrgForRequests(): RequestOrg {
  const { activeOrgId, sessionClientId } = useOrgContextStore.getState();
  const current = readClientId();
  if (sessionClientId !== null && current !== sessionClientId) {
    return { kind: "stale" };
  }
  if (sessionClientId === null && current !== null) {
    useOrgContextStore.setState({ sessionClientId: current });
  }
  return activeOrgId === null
    ? { kind: "unscoped" }
    : { kind: "org", orgId: activeOrgId };
}
